// @snake-case-api Terraform module variables use snake_case names.
import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useOrganization } from '@agion/auth'
import { SingleVmWizard } from './SingleVmWizard'
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  Checkbox,
  EditableCombobox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  EmptyState,
} from '@agion/ui'
import { CheckCircle2, Database, Rocket, Server, ExternalLink, Plus, Trash2 } from 'lucide-react'
import { AdvancedSettings } from './AdvancedSettings'
import { parseSshAllowedCidrs } from './advancedSettingsUtils'
import { PageHeader } from '@/layouts/InfraPageHeader'
import type { HostGroup, CloudProvider } from '@/core/infra_types'
import { listHostGroups, generateBootstrapToken, listPendingHosts, regenerateHostToken, type PendingHost } from './infra_governance.service'
import { listCredentials } from '@/features/secondary/infra_credential.service'
import type { CloudCredential } from '@/features/secondary/infra_credential.service'
import { listSshKeys } from '@/features/secondary/infra_ssh_key.service'
import type { SshKey } from '@/features/secondary/infra_ssh_key.service'
import { listTerraformModules, getModuleVersions } from '@/features/terraform/infra_terraform.service'
import type { TerraformModule } from '@/features/terraform/infra_terraform.service'
import { listImages } from '@/features/secondary/infra_image.service'
import { aptChannelFromVersion, channelForEnvironment } from '@/core/infra_utils'
import { getConfig } from '@/core/infra_runtime_env'
import type { PackerImage } from '@/features/secondary/infra_image.service'
import { createEnvironment, listEnvironments } from './infra_environment.service'
import { azureResourceGroupName, includedDiskSizeGb, isSingletonVmProvider } from './vmDeployment'
import {
  AZURE_VM_SIZES,
  AZURE_DB_SKUS,
  GCP_DB_TIERS,
  UPCLOUD_PLANS,
  UPCLOUD_DB_PLANS,
  canEnableJinaReranker,
  gcpVmSizesForRegion,
  upcloudPlanStorageGb,
} from './infra_vm_sizes'
import { createOperation } from '@/features/operation/infra_operation.service'
import { listChartVersions } from '@/features/chart/infra_chart.service'
import type { ChartVersion } from '@/features/chart/infra_chart.service'

const GCP_REGIONS = [
  { value: 'europe-north1', label: 'Europe North (Finland)' },
  { value: 'europe-west1', label: 'Europe West (Belgium)' },
  { value: 'europe-west4', label: 'Europe West (Netherlands)' },
  { value: 'us-east1', label: 'US East (South Carolina)' },
  { value: 'us-central1', label: 'US Central (Iowa)' },
]

const UPCLOUD_ZONES = [
  { value: 'fi-hel1', label: 'Helsinki 1 (Finland)' },
  { value: 'fi-hel2', label: 'Helsinki 2 (Finland)' },
  { value: 'de-fra1', label: 'Frankfurt (Germany)' },
  { value: 'nl-ams1', label: 'Amsterdam (Netherlands)' },
  { value: 'uk-lon1', label: 'London (UK)' },
  { value: 'us-nyc1', label: 'New York (US)' },
]

const AZURE_REGIONS = [
  { value: 'swedencentral', label: 'Sweden Central' },
  { value: 'northeurope', label: 'North Europe (Ireland)' },
  { value: 'westeurope', label: 'West Europe (Netherlands)' },
]

// VM-size and DB-plan catalogs live in `./infra_vm_sizes` so this `.tsx`
// file only exports the page component (required for React Fast Refresh
// under `react-refresh/only-export-components`). See that module for the
// full rationale and family-level docs.

function defaultsForCloudProvider(provider?: CloudProvider) {
  if (provider === 'azure') {
    return {
      region: 'swedencentral',
      // Standard_D4s_v5 is the documented production Edge tier (see
      // docs/09-runbooks/azure-edge-onboarding.md) and draws from the
      // "Standard Dsv5 Family vCPUs" quota the onboarding preflight checks.
      // 4 vCPU keeps the reranker available, matching the GCP and UpCloud
      // defaults; operators downshift to Standard_D2s_v5 for test envs.
      vmSize: 'Standard_D4s_v5',
      dbTier: 'B_Standard_B2s',
    }
  }
  if (provider === 'upcloud') {
    return {
      region: 'fi-hel1',
      // PREMIUM-4xCPU-16GB is the inference-recommended baseline (Premium 4
      // vCPU, 16 GB, 150 GB · AVX-512 if Turin). The cheaper 2xCPU-4GB plan
      // is below the cross-encoder reranker + embedding-model footprint
      // (~2.5 GB resident) so it gets surfaced in the dropdown but is not
      // the default. See infra_vm_sizes.ts inference-readiness invariant.
      vmSize: 'PREMIUM-4xCPU-16GB',
      dbTier: '1x2xCPU-4GB-50GB',
    }
  }
  return {
    region: 'europe-north1',
    vmSize: 'e2-standard-4',
    dbTier: 'db-custom-2-8192',
  }
}

type WizardMode = 'host_group' | 'single_host'

// Advanced settings component lives in ./AdvancedSettings.tsx — shared with
// the re-apply dialog so env.Config rehydrates deterministically.

export function OnboardingWizardPage() {
  const { t } = useTranslation('pages')
  const { active } = useOrganization()
  const [mode, setMode] = useState<'single' | 'group'>('single')
  return <div className="container-standard space-y-6">
    <PageHeader icon={Plus} title={t('onboarding.title')} description={t('vmWizard.description', 'Configure, review and provision a cloud VM.')} />
    <div className="flex flex-wrap gap-2" role="group" aria-label={t('onboarding.mode.label')}>
      <Button variant={mode === 'single' ? 'primary' : 'outline'} aria-pressed={mode === 'single'} onClick={() => setMode('single')}>{t('vmWizard.singleVm', 'Single VM')}</Button>
      <Button variant={mode === 'group' ? 'primary' : 'outline'} aria-pressed={mode === 'group'} onClick={() => setMode('group')}>{t('vmWizard.groupMode', 'Host groups / DC')}</Button>
    </div>
    {!active ? <Alert variant="warning">{t('vmWizard.selectOrganization', 'Select an organization before configuring an environment.')}</Alert>
      : mode === 'single' ? <SingleVmWizard key={active.id} organization={active} />
        : <><Alert variant="warning">{t('vmWizard.haLimitations', 'Host-group provisioning is an advanced path. Azure currently supports a single VM only; selecting a group does not enable Azure HA.')}</Alert><LegacyOnboardingWizardPage hostGroupsOnly /></>}
  </div>
}

export function LegacyOnboardingWizardPage({ hostGroupsOnly = false }: { hostGroupsOnly?: boolean }) {
  const { t } = useTranslation(['pages'])
  const navigate = useNavigate()
  const [isDeploying, setIsDeploying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [deploySuccess, setDeploySuccess] = useState(false)
  const [operationId, setOperationId] = useState<string | null>(null)

  // Wizard mode: host_group (clusters/HA) or single_host (pending hosts)
  const [wizardMode, setWizardMode] = useState<WizardMode>('host_group')

  // Data from APIs
  const [hostGroups, setHostGroups] = useState<HostGroup[]>([])
  const [pendingHosts, setPendingHosts] = useState<PendingHost[]>([])
  const [credentials, setCredentials] = useState<CloudCredential[]>([])
  const [sshKeys, setSshKeys] = useState<SshKey[]>([])
  const [chartVersions, setChartVersions] = useState<ChartVersion[]>([])
  const [terraformModules, setTerraformModules] = useState<TerraformModule[]>([])
  const [packerImages, setPackerImages] = useState<PackerImage[]>([])
  const [loadingHostGroups, setLoadingHostGroups] = useState(true)
  const [hostGroupsError, setHostGroupsError] = useState(false)
  const [hostGroupsAttempt, setHostGroupsAttempt] = useState(0)
  const [pendingHostsError, setPendingHostsError] = useState(false)
  const [pendingHostsAttempt, setPendingHostsAttempt] = useState(0)
  const [loadingPendingHosts, setLoadingPendingHosts] = useState(false)
  const [loadingCredentials, setLoadingCredentials] = useState(false)
  const [loadingSshKeys, setLoadingSshKeys] = useState(false)
  const [loadingImages, setLoadingImages] = useState(false)

  // Common form state
  const [hostGroupId, setHostGroupId] = useState('')
  const [selectedHostId, setSelectedHostId] = useState('')
  const [chartVersion, setChartVersion] = useState('')

  // Cloud form state
  const [credentialId, setCredentialId] = useState('')
  const [region, setRegion] = useState(defaultsForCloudProvider('gcp').region)
  const [vmSize, setVmSize] = useState(defaultsForCloudProvider('gcp').vmSize)
  const [jinaRerankerEnabled, setJinaRerankerEnabled] = useState(false)
  // /16 matches azure-vm module defaults (subnet=10.0.0.0/25, db=10.0.0.128/25,
  // dfs_pe=10.0.1.0/28 — the last doesn't fit inside /24). Safe for GCP too.
  const [networkCidr, setNetworkCidr] = useState('10.0.0.0/16')
  const [replicaCount, setReplicaCount] = useState(1)

  // Managed database state
  const [managedDbEnabled, setManagedDbEnabled] = useState(false)
  const [dbTier, setDbTier] = useState(defaultsForCloudProvider('gcp').dbTier)
  const [separateIntelligenceDb, setSeparateIntelligenceDb] = useState(false)
  const [intelligenceDbTier, setIntelligenceDbTier] = useState('')

  // Advanced settings (progressive disclosure). Defaults match terraform
  // module defaults EXCEPT dfsEnabled/diagnosticsEnabled, which are ON for
  // new environments — the durable file store and per-deployment Log
  // Analytics are first-class platform features, not opt-ins. Empty
  // strings for zone/diskSizeGb/logRetentionDays mean "no override".
  const [advancedOpen, setAdvancedOpen] = useState(false)
  // GCP-only zone (string like "europe-north1-b").
  const [zone, setZone] = useState<string>('')
  // Shared with Azure.
  const [diskSizeGb, setDiskSizeGb] = useState<string>('')
  // A new UpCloud VM gets its plan's included storage until the operator sets a size.
  const diskChosen = useRef(false)
  const [dfsEnabled, setDfsEnabled] = useState<boolean>(true)
  // Azure-only. availabilityZones is list-shaped now so Track C can spread
  // across multiple zones without an interface rename. UI renders it as a
  // single-select; the state is a 0-or-1 element list.
  const [availabilityZones, setAvailabilityZones] = useState<string[]>([])
  const [dfsReplication, setDfsReplication] = useState<string>('LRS')
  const [diagnosticsEnabled, setDiagnosticsEnabled] = useState<boolean>(true)
  const [logRetentionDays, setLogRetentionDays] = useState<string>('30')
  const [sshAllowedCidrs, setSshAllowedCidrs] = useState<string>('*')

  // On-prem (k3s_bare) form state
  const [bastionIp, setBastionIp] = useState('')
  const [nodes, setNodes] = useState<Array<{ ip: string; storage: boolean }>>([
    { ip: '', storage: false },
    { ip: '', storage: false },
    { ip: '', storage: true },  // Third node defaults to storage (customer's ai-db01 pattern)
  ])
  const [sshKeyId, setSshKeyId] = useState('')
  const [sshUser, setSshUser] = useState('agion')
  const [sudoPassword, setSudoPassword] = useState('')
  const [tfModuleVersion, setTfModuleVersion] = useState('')
  const [packerImageId, setPackerImageId] = useState('')

  const selectedHostGroup = hostGroups.find(g => g.id === hostGroupId)
  const selectedCredential = credentials.find(c => c.id === credentialId)
  const selectedSshKey = sshKeys.find(k => k.id === sshKeyId)
  const isOnprem = selectedHostGroup?.deploymentType === 'onprem'
  const isKubernetes = selectedHostGroup?.deploymentType === 'kubernetes'
  const isCloudK8s = isKubernetes && !!selectedHostGroup?.cloudProvider
  const isOnpremK8s = isKubernetes && !selectedHostGroup?.cloudProvider
  // On-prem bare metal and on-prem K8s both need SSH access for provisioning
  const needsSSHAccess = isOnprem || isOnpremK8s

  // Single host mode: filter pending hosts with cloud_provider metadata
  // keysToCamel converts metadata keys: cloud_provider → cloudProvider
  // Check both forms for safety (CLAUDE.md 0.3)
  const getCloudProvider = (h: PendingHost): string | undefined =>
    (h.metadata?.['cloudProvider'] ?? h.metadata?.['cloud_provider']) as string | undefined
  const provisionablePendingHosts = pendingHosts.filter(h => getCloudProvider(h))
  const selectedPendingHost = pendingHosts.find(h => h.id === selectedHostId)
  const singleHostCloudProvider = getCloudProvider(selectedPendingHost ?? { id: '', name: '', hostType: 'vpn', status: '', createdAt: '' }) as CloudProvider | undefined
  const planDiskSizeGb = (provider: CloudProvider | undefined, size: string) =>
    includedDiskSizeGb(provider === 'upcloud' ? provider : undefined, size)
  const applyDefaultDisk = (provider: CloudProvider | undefined, size: string) => {
    diskChosen.current = false
    setDiskSizeGb(planDiskSizeGb(provider, size))
  }
  const chooseVmSize = (size: string) => {
    setVmSize(size)
    const provider = selectedHostGroup?.cloudProvider ?? singleHostCloudProvider
    if (provider === 'upcloud' && !diskChosen.current) setDiskSizeGb(planDiskSizeGb(provider, size))
  }
  const chooseDiskSize = (size: string) => {
    if (size !== diskSizeGb) diskChosen.current = true
    setDiskSizeGb(size)
  }
  const jinaRerankerAvailable = canEnableJinaReranker(vmSize)
  const effectiveJinaRerankerEnabled = jinaRerankerAvailable && jinaRerankerEnabled

  // Map host group type to terraform module name
  const tfModuleName = isOnprem || isOnpremK8s ? 'k3s-cluster'
    : isCloudK8s ? `${selectedHostGroup?.cloudProvider}-gke`
    : selectedHostGroup?.cloudProvider ? `${selectedHostGroup.cloudProvider}-vm`
    : singleHostCloudProvider ? `${singleHostCloudProvider}-vm`
    : undefined

  // Load host groups and pending hosts on mount
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        setLoadingHostGroups(true)
        setHostGroupsError(false)
        const groups = await listHostGroups()
        if (!cancelled) setHostGroups(groups)
      } catch {
        if (!cancelled) setHostGroupsError(true)
      } finally {
        if (!cancelled) setLoadingHostGroups(false)
      }
    })()
    return () => { cancelled = true }
  }, [hostGroupsAttempt])

  useEffect(() => {
    if (!jinaRerankerAvailable && jinaRerankerEnabled) {
      setJinaRerankerEnabled(false)
    }
  }, [jinaRerankerAvailable, jinaRerankerEnabled])

  // Load pending hosts when switching to single_host mode.
  // Filter out hosts that already have an environment (host_id in env config).
  useEffect(() => {
    if (wizardMode !== 'single_host') return
    let cancelled = false
    void (async () => {
      try {
        setLoadingPendingHosts(true)
        setPendingHostsError(false)
        const [hosts, envResp] = await Promise.all([
          listPendingHosts(),
          listEnvironments({ perPage: 100 }),
        ])
        // Collect host IDs that already have environments
        const provisionedHostIds = new Set(
          (envResp.data ?? [])
            .map(e => String(e.config?.['hostId'] ?? e.config?.['host_id'] ?? ''))
            .filter(Boolean)
        )
        if (!cancelled) setPendingHosts(hosts.filter(h => !provisionedHostIds.has(h.id)))
      } catch {
        if (!cancelled) setPendingHostsError(true)
      } finally {
        if (!cancelled) setLoadingPendingHosts(false)
      }
    })()
    return () => { cancelled = true }
  }, [wizardMode, pendingHostsAttempt])

  // Auto-select the newest terraform module tag once modules load. Without
  // this, the wizard sends no module_version (it's only spread when truthy)
  // and the infra-service enqueue gate rejects the apply with "terraform_apply
  // requires parameters.variables.module_version". The dropdown stays open
  // so operators can still pick an older tag if they need to.
  useEffect(() => {
    if (!tfModuleName) return
    if (tfModuleVersion) return
    const versions = getModuleVersions(terraformModules, tfModuleName)
    if (versions.length > 0) setTfModuleVersion(versions[0]!)
  }, [terraformModules, tfModuleName, tfModuleVersion])

  // Load credentials when cloud host group changes
  const loadCredentials = useCallback(async (provider?: CloudProvider) => {
    try {
      setLoadingCredentials(true)
      const resp = await listCredentials({ cloudProvider: provider, status: 'active' })
      setCredentials(resp.data ?? [])
    } catch {
      setCredentials([])
    } finally {
      setLoadingCredentials(false)
    }
  }, [])

  // Load SSH keys for selection.
  // onpremOnly=true filters to keys with private key stored (Terraform needs it to SSH in).
  // onpremOnly=false shows all keys (cloud VMs only need the public key).
  const loadSshKeys = useCallback(async (onpremOnly = false) => {
    try {
      setLoadingSshKeys(true)
      const resp = await listSshKeys()
      const all = resp.data ?? []
      setSshKeys(onpremOnly ? all.filter(k => k.vaultPath) : all)
    } catch {
      setSshKeys([])
    } finally {
      setLoadingSshKeys(false)
    }
  }, [])

  useEffect(() => {
    if (!selectedHostGroup) return

    setCredentialId('')
    setSshKeyId('')
    setChartVersion('')
    setTfModuleVersion('')
    setPackerImageId('')
    setManagedDbEnabled(false)
    setSeparateIntelligenceDb(false)

    const provider = selectedHostGroup.cloudProvider
    const defaults = defaultsForCloudProvider(provider)
    setRegion(defaults.region)
    setVmSize(defaults.vmSize)
    applyDefaultDisk(provider, defaults.vmSize)
    setDbTier(defaults.dbTier)
    setIntelligenceDbTier('')

    // Set default replica count. Azure and UpCloud force 1 because azure-vm
    // and upcloud-vm each build a single VM (Track C will lift Azure); other
    // providers default to 3 for active_active and 1 otherwise.
    const defaultReplicas = isSingletonVmProvider(selectedHostGroup.cloudProvider)
      ? 1
      : selectedHostGroup.mode === 'active_active' ? 3 : 1
    setReplicaCount(defaultReplicas)

    // Always load terraform modules and chart versions (all paths need them)
    void listTerraformModules().then(setTerraformModules).catch(() => setTerraformModules([]))
    void listChartVersions().then(setChartVersions).catch(() => setChartVersions([]))

    if (needsSSHAccess) {
      void loadSshKeys(true) // on-prem: only keys with private key stored
    } else if (selectedHostGroup.cloudProvider) {
      void loadCredentials(selectedHostGroup.cloudProvider)
      // Also load SSH keys for cloud VMs — platform-generated keys used for host access
      void loadSshKeys(false) // cloud: all keys (only public key needed)

      // Load available packer images for VM deployments
      if (!isKubernetes) {
        void (async () => {
          try {
            setLoadingImages(true)
            const resp = await listImages({ cloudProvider: selectedHostGroup.cloudProvider!, status: 'available' })
            const envChannel = channelForEnvironment(getConfig('VITE_ENVIRONMENT'))
            const all = resp.data ?? []
            setPackerImages(envChannel ? all.filter(img => aptChannelFromVersion(img.version) === envChannel) : all)
          } catch {
            setPackerImages([])
          } finally {
            setLoadingImages(false)
          }
        })()
      }
    }
  }, [selectedHostGroup?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load credentials when a single host is selected (single_host mode)
  useEffect(() => {
    if (wizardMode !== 'single_host' || !selectedPendingHost) return

    setCredentialId('')
    setChartVersion('')
    setTfModuleVersion('')
    setPackerImageId('')
    setReplicaCount(1) // Single hosts are always 1 replica
    setManagedDbEnabled(false)
    setSeparateIntelligenceDb(false)

    const defaults = defaultsForCloudProvider(singleHostCloudProvider)
    setRegion(defaults.region)
    setVmSize(defaults.vmSize)
    applyDefaultDisk(singleHostCloudProvider, defaults.vmSize)
    setDbTier(defaults.dbTier)
    setIntelligenceDbTier('')

    // Load terraform modules (VM only for single hosts)
    void listTerraformModules().then(setTerraformModules).catch(() => setTerraformModules([]))

    if (singleHostCloudProvider) {
      void loadCredentials(singleHostCloudProvider)
      void loadSshKeys(false) // cloud: all keys (only public key needed)

      // Load available packer images
      void (async () => {
        try {
          setLoadingImages(true)
          const resp = await listImages({ cloudProvider: singleHostCloudProvider, status: 'available' })
          const envChannel = channelForEnvironment(getConfig('VITE_ENVIRONMENT'))
          const all = resp.data ?? []
          setPackerImages(envChannel ? all.filter(img => aptChannelFromVersion(img.version) === envChannel) : all)
        } catch {
          setPackerImages([])
        } finally {
          setLoadingImages(false)
        }
      })()
    }
  }, [selectedPendingHost?.id, wizardMode]) // eslint-disable-line react-hooks/exhaustive-deps

  // Validation for host group mode
  const canDeployCloud = hostGroupId !== ''
    && credentialId !== ''
    && region !== ''
    && vmSize !== ''
    && selectedHostGroup?.cloudProvider

  const filteredNodes = nodes.filter(n => n.ip.trim() !== '')
  const hasStorageNode = filteredNodes.some(n => n.storage)
  const canDeployOnprem = hostGroupId !== ''
    && sshKeyId !== ''
    && bastionIp.trim() !== ''
    && filteredNodes.length >= 3
    && hasStorageNode

  // Validation for single host mode
  const canDeploySingleHost = selectedHostId !== ''
    && credentialId !== ''
    && region !== ''
    && vmSize !== ''
    && singleHostCloudProvider

  const canDeploy = wizardMode === 'single_host'
    ? canDeploySingleHost
    : (needsSSHAccess ? canDeployOnprem : canDeployCloud)

  function addNode() {
    setNodes(prev => [...prev, { ip: '', storage: false }])
  }

  function removeNode(index: number) {
    setNodes(prev => prev.filter((_, i) => i !== index))
  }

  function updateNodeIp(index: number, value: string) {
    setNodes(prev => prev.map((node, i) => i === index ? { ...node, ip: value } : node))
  }

  function toggleStorage(index: number) {
    setNodes(prev => prev.map((node, i) => i === index ? { ...node, storage: !node.storage } : node))
  }

  async function handleDeploy() {
    try {
      setIsDeploying(true)
      setError(null)

      let opId: string | undefined

      if (wizardMode === 'single_host') {
        if (!selectedPendingHost) return
        opId = await handleDeploySingleHost()
      } else {
        if (!selectedHostGroup) return
        if (needsSSHAccess) {
          opId = await handleDeployOnprem()
        } else {
          opId = await handleDeployCloud()
        }
      }

      // Redirect to operation logs for live progress tracking
      if (opId) {
        navigate(`/operations/${opId}`)
        return
      }
      setDeploySuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Deployment failed')
    } finally {
      setIsDeploying(false)
    }
  }

  async function handleDeployOnprem(): Promise<string | undefined> {
    if (!selectedHostGroup || !selectedSshKey) return undefined

    const validNodes = nodes.filter(n => n.ip.trim() !== '')
    const k3sNodes = validNodes.map(n => ({
      ip: n.ip.trim(),
      labels: {
        ...(n.storage ? { 'agion.dev/storage': 'true' } : {}),
      },
    }))

    const deploymentModel = 'k3s_bare'

    const env = await createEnvironment({
      name: selectedHostGroup.name,
      region: 'onprem',
      deploymentModel,
      hostGroupId: selectedHostGroup.id,
      config: {
        bastion_ip: bastionIp.trim(),
        init_server_ip: validNodes[0]?.ip.trim() || '',
        nodes: k3sNodes,
        ssh_user: sshUser,
        ssh_key_id: sshKeyId,
        cluster_mode: validNodes.length >= 3 ? 'ha' : 'single',
        ...(sudoPassword ? { sudo_password_set: true } : {}),
        ...(chartVersion ? { chart_version: chartVersion } : {}),
      },
    })

    // Generate bootstrap token for helm install
    const bootstrap = await generateBootstrapToken(selectedHostGroup.id)

    const op = await createOperation({
      environmentId: env.id,
      operationType: 'provision',
      parameters: {
        module: 'k3s-cluster',
        chart_version: chartVersion || '',
        bootstrap_token: bootstrap.bootstrapToken || '',
        values_preset: 'k3s-onprem',
        variables: {
          // module_version must sit inside `variables` — the infra-service
          // enqueue gate reads `parameters.variables.module_version` and
          // rejects a missing value to block the implicit :latest fallback.
          ...(tfModuleVersion ? { module_version: tfModuleVersion } : {}),
          bastion_ip: bastionIp.trim(),
          bastion_user: sshUser,
          nodes_json: JSON.stringify(k3sNodes),
          ssh_user: sshUser,
          ssh_key_vault_path: selectedSshKey.vaultPath ?? '',
          environment_name: env.name,
          cluster_mode: validNodes.length >= 3 ? 'ha' : 'single',
          ...(sudoPassword ? { sudo_password: sudoPassword } : {}),
        },
      },
    })

    setOperationId(op.id)
    return op.id
  }

  async function handleDeployCloud(): Promise<string | undefined> {
    if (!selectedHostGroup || !selectedCredential) return undefined

    const deploymentModel = isKubernetes ? 'k8s_edge' : replicaCount === 1 ? 'vm_single' : 'vm_ha'

    const selectedImage = packerImages.find(img => img.id === packerImageId)

    const env = await createEnvironment({
      name: selectedHostGroup.name,
      cloudProvider: selectedHostGroup.cloudProvider!,
      region,
      deploymentModel,
      hostGroupId: selectedHostGroup.id,
      credentialId: selectedCredential.id,
      config: {
        vm_size: vmSize,
        network_cidr: networkCidr,
        replica_count: replicaCount,
        bastion_enabled: true,
        managed_db_enabled: managedDbEnabled,
        // Advanced settings — camelCase so the Apply dialog rehydrates
        // deterministically. GCP writes `zone`; Azure writes
        // `availabilityZones` and extras; keep them separate so debugging
        // env.Config JSON never mixes GCP region-b zones with Azure AZ
        // numbers (they have the same field slot if collapsed).
        ...(selectedHostGroup.cloudProvider === 'azure' ? {
          availabilityZones,
          diskSizeGb,
          dfsEnabled,
          dfsReplication,
          diagnosticsEnabled,
          logRetentionDays,
          sshAllowedCidrs: parseSshAllowedCidrs(sshAllowedCidrs),
        } : {
          zone,
          diskSizeGb,
          dfsEnabled,
        }),
        ...(sshKeyId ? { ssh_key_id: sshKeyId } : {}),
        ...(managedDbEnabled ? { db_tier: dbTier } : {}),
        ...(managedDbEnabled ? { separate_intelligence_db: separateIntelligenceDb } : {}),
        ...(managedDbEnabled && separateIntelligenceDb ? { intelligence_db_tier: intelligenceDbTier } : {}),
        ...(!isKubernetes ? { jina_reranker_enabled: effectiveJinaRerankerEnabled } : {}),
        ...(selectedImage ? { packer_image_id: selectedImage.id, packer_image_version: selectedImage.version } : {}),
        ...(chartVersion ? { chart_version: chartVersion } : {}),
        ...(tfModuleVersion ? { tf_module_version: tfModuleVersion } : {}),
      },
    })

    const module = tfModuleName ?? (isKubernetes ? 'gcp-gke' : 'gcp-vm')

    // Generate bootstrap token for the host group
    const bootstrap = await generateBootstrapToken(selectedHostGroup.id)
    if (!bootstrap.bootstrapConfig && !bootstrap.bootstrapToken) {
      throw new Error('Failed to generate bootstrap config for host group')
    }

    // Use 'provision' for k8s deployments (terraform + helm), 'terraform_apply' for VMs
    const operationType = isKubernetes ? 'provision' : 'terraform_apply'
    const valuesPreset = isKubernetes
      ? (selectedHostGroup.cloudProvider === 'gcp' ? 'gcp' : selectedHostGroup.cloudProvider === 'aws' ? 'aws' : 'k3s-onprem')
      : undefined

    const op = await createOperation({
      environmentId: env.id,
      operationType,
      parameters: {
        module,
        ...(isKubernetes ? { chart_version: chartVersion || '' } : {}),
        ...(isKubernetes ? { bootstrap_token: bootstrap.bootstrapToken || '' } : {}),
        ...(valuesPreset ? { values_preset: valuesPreset } : {}),
        variables: {
          // module_version must sit inside `variables` — the infra-service
          // enqueue gate reads `parameters.variables.module_version` and
          // rejects a missing value to block the implicit :latest fallback.
          ...(tfModuleVersion ? { module_version: tfModuleVersion } : {}),
          environment_name: env.name,
          vm_size: vmSize,
          ...(!isKubernetes ? { jina_reranker_enabled: String(effectiveJinaRerankerEnabled) } : {}),
          replica_count: String(replicaCount),
          bootstrap_config: bootstrap.bootstrapConfig ?? bootstrap.bootstrapToken ?? '',
          // GCP-specific variables
          ...(selectedHostGroup.cloudProvider === 'gcp' ? {
            project_id: (selectedCredential.metadata as Record<string, string>)['project_id'] || '',
            region,
            ha_mode: selectedHostGroup.mode ?? 'active_active',
            network_cidr: networkCidr,
            bastion_enabled: 'true',
            managed_db_enabled: String(managedDbEnabled),
            ...(managedDbEnabled ? { db_tier: dbTier } : {}),
            ...(managedDbEnabled ? { separate_intelligence_db: String(separateIntelligenceDb) } : {}),
            ...(managedDbEnabled && separateIntelligenceDb ? { intelligence_db_tier: intelligenceDbTier } : {}),
            ...(selectedImage?.name ? { image_name: selectedImage.name } : {}),
            // Advanced settings: zone/disk only forwarded on explicit
            // override; dfs_enabled is always explicit because its state
            // directly gates GCS + SA + IAM resource creation.
            ...(zone ? { zone } : {}),
            ...(diskSizeGb ? { disk_size_gb: diskSizeGb } : {}),
            dfs_enabled: String(dfsEnabled),
          } : {}),
          // UpCloud-specific variables
          ...(selectedHostGroup.cloudProvider === 'upcloud' ? {
            zone: region,
            plan: vmSize,
            template_name: selectedImage?.name ?? 'agion-byoh-runtime',
            managed_db_enabled: String(managedDbEnabled),
            ...(managedDbEnabled ? { db_plan: dbTier } : {}),
            ...(managedDbEnabled ? { separate_intelligence_db: String(separateIntelligenceDb) } : {}),
            ...(managedDbEnabled && separateIntelligenceDb ? { intelligence_db_plan: intelligenceDbTier } : {}),
            dfs_enabled: String(dfsEnabled),
            ...(diskSizeGb ? { disk_size_gb: diskSizeGb } : {}),
          } : {}),
          // Azure-specific variables. List-shaped vars use the `_json`
          // transport convention — infra-service extractJSONVariables
          // rewrites them to typed list(string) HCL vars at tfvars write
          // time. Advanced scalar fields (dfs_enabled, diagnostics_enabled,
          // dfs_replication, log_retention_days) are ALWAYS forwarded so
          // decommission replay and apply rehydration stay deterministic.
          ...(selectedHostGroup.cloudProvider === 'azure' ? {
            location: region,
            resource_group_name: azureResourceGroupName('', env.name),
            subscription_id: (selectedCredential.metadata as Record<string, string>)['subscription_id'] || '',
            managed_db_enabled: String(managedDbEnabled),
            ...(managedDbEnabled ? { db_sku: dbTier } : {}),
            ...(managedDbEnabled ? { separate_intelligence_db: String(separateIntelligenceDb) } : {}),
            ...(managedDbEnabled && separateIntelligenceDb ? { intelligence_db_sku: intelligenceDbTier } : {}),
            ...(selectedImage?.name ? { image_name: selectedImage.name } : {}),
            // Track A3 advanced fields
            availability_zones_json: JSON.stringify(availabilityZones),
            ssh_allowed_cidrs_json: JSON.stringify(parseSshAllowedCidrs(sshAllowedCidrs)),
            dfs_enabled: String(dfsEnabled),
            dfs_replication: dfsReplication,
            diagnostics_enabled: String(diagnosticsEnabled),
            log_retention_days: logRetentionDays,
            ...(diskSizeGb ? { disk_size_gb: diskSizeGb } : {}),
          } : {}),
        },
      },
    })

    setOperationId(op.id)
    return op.id
  }

  async function handleDeploySingleHost(): Promise<string | undefined> {
    if (!selectedPendingHost || !selectedCredential || !singleHostCloudProvider) return undefined

    const deploymentModel = 'vm_single'
    const selectedImage = packerImages.find(img => img.id === packerImageId)

    // Create environment record linked to the single host
    const env = await createEnvironment({
      name: selectedPendingHost.name,
      cloudProvider: singleHostCloudProvider,
      region,
      deploymentModel,
      credentialId: selectedCredential.id,
      config: {
        vm_size: vmSize,
        jina_reranker_enabled: effectiveJinaRerankerEnabled,
        network_cidr: networkCidr,
        replica_count: 1,
        host_id: selectedPendingHost.id, // Link to the pending host
        bastion_enabled: true,
        managed_db_enabled: managedDbEnabled,
        // Advanced settings — same provider-aware shape as the host_group
        // path. Keep this block in sync with handleDeployCloud's env.Config.
        ...(singleHostCloudProvider === 'azure' ? {
          availabilityZones,
          diskSizeGb,
          dfsEnabled,
          dfsReplication,
          diagnosticsEnabled,
          logRetentionDays,
          sshAllowedCidrs: parseSshAllowedCidrs(sshAllowedCidrs),
        } : {
          zone,
          diskSizeGb,
          dfsEnabled,
        }),
        ...(sshKeyId ? { ssh_key_id: sshKeyId } : {}),
        ...(managedDbEnabled ? { db_tier: dbTier } : {}),
        ...(managedDbEnabled ? { separate_intelligence_db: separateIntelligenceDb } : {}),
        ...(managedDbEnabled && separateIntelligenceDb ? { intelligence_db_tier: intelligenceDbTier } : {}),
        ...(selectedImage ? { packer_image_id: selectedImage.id, packer_image_version: selectedImage.version } : {}),
        ...(tfModuleVersion ? { tf_module_version: tfModuleVersion } : {}),
      },
    })

    const module = `${singleHostCloudProvider}-vm`

    // Regenerate bootstrap token for the single host (original may have expired)
    const bootstrap = await regenerateHostToken(selectedPendingHost.id)
    if (!bootstrap.bootstrapConfig && !bootstrap.bootstrapToken) {
      throw new Error('Failed to generate bootstrap config for host')
    }

    const op = await createOperation({
      environmentId: env.id,
      operationType: 'terraform_apply',
      parameters: {
        module,
        variables: {
          // module_version must sit inside `variables` — the infra-service
          // enqueue gate reads `parameters.variables.module_version` and
          // rejects a missing value to block the implicit :latest fallback.
          ...(tfModuleVersion ? { module_version: tfModuleVersion } : {}),
          environment_name: env.name,
          vm_size: vmSize,
          jina_reranker_enabled: String(effectiveJinaRerankerEnabled),
          replica_count: '1',
          bootstrap_config: bootstrap.bootstrapConfig ?? bootstrap.bootstrapToken ?? '',
          // GCP-specific variables
          ...(singleHostCloudProvider === 'gcp' ? {
            project_id: (selectedCredential.metadata as Record<string, string>)['project_id'] || '',
            region,
            ha_mode: 'standalone',
            network_cidr: networkCidr,
            bastion_enabled: 'true',
            managed_db_enabled: String(managedDbEnabled),
            ...(managedDbEnabled ? { db_tier: dbTier } : {}),
            ...(managedDbEnabled ? { separate_intelligence_db: String(separateIntelligenceDb) } : {}),
            ...(managedDbEnabled && separateIntelligenceDb ? { intelligence_db_tier: intelligenceDbTier } : {}),
            ...(selectedImage?.name ? { image_name: selectedImage.name } : {}),
            ...(zone ? { zone } : {}),
            ...(diskSizeGb ? { disk_size_gb: diskSizeGb } : {}),
            dfs_enabled: String(dfsEnabled),
          } : {}),
          // UpCloud-specific variables
          ...(singleHostCloudProvider === 'upcloud' ? {
            zone: region,
            plan: vmSize,
            template_name: selectedImage?.name ?? 'agion-byoh-runtime',
            managed_db_enabled: String(managedDbEnabled),
            ...(managedDbEnabled ? { db_plan: dbTier } : {}),
            ...(managedDbEnabled ? { separate_intelligence_db: String(separateIntelligenceDb) } : {}),
            ...(managedDbEnabled && separateIntelligenceDb ? { intelligence_db_plan: intelligenceDbTier } : {}),
            dfs_enabled: String(dfsEnabled),
            ...(diskSizeGb ? { disk_size_gb: diskSizeGb } : {}),
          } : {}),
          // Azure-specific variables — mirrors handleDeployCloud's Azure branch.
          // Keep the two in sync; both feed the same azure-vm module.
          ...(singleHostCloudProvider === 'azure' ? {
            location: region,
            resource_group_name: azureResourceGroupName('', env.name),
            subscription_id: (selectedCredential.metadata as Record<string, string>)['subscription_id'] || '',
            managed_db_enabled: String(managedDbEnabled),
            ...(managedDbEnabled ? { db_sku: dbTier } : {}),
            ...(managedDbEnabled ? { separate_intelligence_db: String(separateIntelligenceDb) } : {}),
            ...(managedDbEnabled && separateIntelligenceDb ? { intelligence_db_sku: intelligenceDbTier } : {}),
            ...(selectedImage?.name ? { image_name: selectedImage.name } : {}),
            // Track A3 advanced fields
            availability_zones_json: JSON.stringify(availabilityZones),
            ssh_allowed_cidrs_json: JSON.stringify(parseSshAllowedCidrs(sshAllowedCidrs)),
            dfs_enabled: String(dfsEnabled),
            dfs_replication: dfsReplication,
            diagnostics_enabled: String(diagnosticsEnabled),
            log_retention_days: logRetentionDays,
            ...(diskSizeGb ? { disk_size_gb: diskSizeGb } : {}),
          } : {}),
        },
      },
    })

    setOperationId(op.id)
    return op.id
  }

  // ============= Success Screen =============

  if (deploySuccess) {
    return (
      <div className="p-6 space-y-6">
        <PageHeader
          icon={Plus}
          title={t('pages:onboarding.title', 'New Environment')}
          description={t('pages:onboarding.description', 'Provision a new cloud environment')}
        />
        <Card>
          <div className="p-12 text-center space-y-4">
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {t('pages:onboarding.success.title', 'Environment Created')}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
              {t('pages:onboarding.success.description', 'Your environment is being provisioned. You can track progress in the operations view.')}
            </p>
            <div className="flex gap-3 justify-center">
              {operationId && (
                <Button onClick={() => navigate(`/operations/${operationId}`)}>
                  {t('pages:onboarding.success.viewOperation', 'View Operation Logs')}
                </Button>
              )}
              <Button variant="outline" onClick={() => navigate('/environments')}>
                {t('pages:onboarding.success.viewEnvironments', 'All Environments')}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    )
  }

  // ============= Form =============

  return (
    <div className="p-6 space-y-6">
      {!hostGroupsOnly && <PageHeader
        icon={Plus}
        title={t('pages:onboarding.title', 'New Environment')}
        description={t('pages:onboarding.description', 'Provision a new cloud environment')}
      />}

      {error && (
        <Alert variant="error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <div className="p-6 space-y-6 max-w-2xl">
          {/* Mode Toggle: Host Group vs Single Host */}
          {!hostGroupsOnly && <div className="space-y-2">
            <Label>{t('pages:onboarding.mode.label', 'Deployment Type')}</Label>
            <div className="flex gap-2">
              <Button
                variant={wizardMode === 'host_group' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => {
                  setWizardMode('host_group')
                  setSelectedHostId('')
                  setCredentialId('')
                }}
              >
                {t('pages:onboarding.mode.hostGroup', 'Host Group (Cluster/HA)')}
              </Button>
              <Button
                variant={wizardMode === 'single_host' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => {
                  setWizardMode('single_host')
                  setHostGroupId('')
                  setCredentialId('')
                }}
              >
                {t('pages:onboarding.mode.singleHost', 'Single Host')}
              </Button>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {wizardMode === 'host_group'
                ? t('pages:onboarding.mode.hostGroupHint', 'Deploy to a host group with HA/clustering support.')
                : t('pages:onboarding.mode.singleHostHint', 'Provision a pending single host created in Admin UI.')}
            </p>
          </div>

          }
          {/* Host Group Selection (host_group mode) */}
          {wizardMode === 'host_group' && (
          <div className="space-y-2">
            <Label>{t('pages:onboarding.hostGroup.label', 'Host Group')}</Label>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t('pages:onboarding.hostGroup.hint', 'Determines cloud provider, HA strategy, and deployment type.')}
            </p>

            {loadingHostGroups ? (
              <div className="flex justify-center py-4"><Spinner /></div>
            ) : hostGroupsError ? (
              <Alert variant="error">
                <AlertDescription>
                  {t('pages:onboarding.hostGroupsLoadError', 'Failed to load host groups')}
                  <Button variant="outline" size="sm" className="ml-3" onClick={() => setHostGroupsAttempt(attempt => attempt + 1)}>
                    {t('pages:onboarding.retry', 'Retry')}
                  </Button>
                </AlertDescription>
              </Alert>
            ) : hostGroups.filter(g => g.cloudProvider || g.deploymentType === 'onprem' || g.deploymentType === 'kubernetes').length === 0 ? (
              <EmptyState
                icon={Server}
                title={t('pages:onboarding.hostGroup.empty.title', 'No host groups')}
                description={t('pages:onboarding.hostGroup.empty.description', 'Create a host group in the Admin console first.')}
              />
            ) : (
              <div className="grid gap-2">
                {hostGroups.filter(g => g.cloudProvider || g.deploymentType === 'onprem' || g.deploymentType === 'kubernetes').map(group => (
                  <div
                    key={group.id}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                      hostGroupId === group.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                    }`}
                    role="button"
                    tabIndex={0}
                    onClick={() => setHostGroupId(group.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setHostGroupId(group.id) } }}
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{group.name}</p>
                      {group.description && (
                        <p className="text-xs text-slate-500">{group.description}</p>
                      )}
                    </div>
                    <div className="flex gap-1.5">
                      {group.cloudProvider ? (
                        <Badge variant="default">{group.cloudProvider.toUpperCase()}</Badge>
                      ) : (
                        <Badge variant="warning">{t('pages:providers.onPrem')}</Badge>
                      )}
                      <Badge variant="info">{group.deploymentType}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <p className="text-xs text-slate-400">
              <a href="/admin" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-slate-600">
                {t('pages:onboarding.hostGroup.createLink', 'Create a new host group in Admin')}
                <ExternalLink className="h-3 w-3" />
              </a>
            </p>
          </div>
          )}

          {/* Single Host Selection (single_host mode) */}
          {wizardMode === 'single_host' && (
          <div className="space-y-2">
            <Label>{t('pages:onboarding.singleHost.label', 'Pending Host')}</Label>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t('pages:onboarding.singleHost.hint', 'Select a pending host with a cloud provider to provision.')}
            </p>

            {loadingPendingHosts ? (
              <div className="flex justify-center py-4"><Spinner /></div>
            ) : pendingHostsError ? (
              <Alert variant="error">
                <AlertDescription>
                  {t('pages:onboarding.pendingHostsLoadError', 'Failed to load pending hosts')}
                  <Button variant="outline" size="sm" className="ml-3" onClick={() => setPendingHostsAttempt(attempt => attempt + 1)}>
                    {t('pages:onboarding.retry', 'Retry')}
                  </Button>
                </AlertDescription>
              </Alert>
            ) : provisionablePendingHosts.length === 0 ? (
              <EmptyState
                icon={Server}
                title={t('pages:onboarding.singleHost.empty.title', 'No pending hosts')}
                description={t('pages:onboarding.singleHost.empty.description', 'Create a host with a cloud provider in Admin UI first.')}
              />
            ) : (
              <div className="grid gap-2">
                {provisionablePendingHosts.map(host => (
                  <div
                    key={host.id}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedHostId === host.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                    }`}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedHostId(host.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedHostId(host.id) } }}
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{host.name}</p>
                      {host.description && (
                        <p className="text-xs text-slate-500">{host.description}</p>
                      )}
                    </div>
                    <div className="flex gap-1.5">
                      <Badge variant="default">{String(host.metadata?.['cloudProvider'] ?? host.metadata?.['cloud_provider'] ?? '').toUpperCase()}</Badge>
                      <Badge variant="warning">{t('pages:onboarding.singleHost.pending', 'Pending')}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <p className="text-xs text-slate-400">
              <a href="/admin/infrastructure/hosts/add" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-slate-600">
                {t('pages:onboarding.singleHost.createLink', 'Create a new host in Admin')}
                <ExternalLink className="h-3 w-3" />
              </a>
            </p>
          </div>
          )}

          {/* Cloud credential — for cloud host groups */}
          {wizardMode === 'host_group' && selectedHostGroup && !needsSSHAccess && (
            <div className="space-y-2">
              <Label>{t('pages:onboarding.credential.label', 'Cloud Credential')}</Label>

              {loadingCredentials ? (
                <div className="flex justify-center py-4"><Spinner /></div>
              ) : credentials.length === 0 ? (
                <Alert variant="warning">
                  <AlertDescription>
                    {t('pages:onboarding.credential.noCredentials', 'No active credentials found for this cloud provider.')}
                  </AlertDescription>
                </Alert>
              ) : (
                <Select value={credentialId} onValueChange={setCredentialId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('pages:onboarding.credential.select', 'Select credential...')} />
                  </SelectTrigger>
                  <SelectContent>
                    {credentials.map(cred => (
                      <SelectItem key={cred.id} value={cred.id}>
                        {cred.name} ({cred.credentialType})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {selectedCredential && (selectedCredential.metadata as Record<string, string>)['project_id'] && (
                <p className="text-xs text-slate-500">
                  {t('pages:onboarding.credential.project', 'Project')}: {(selectedCredential.metadata as Record<string, string>)['project_id']}
                </p>
              )}
              {selectedCredential && (selectedCredential.metadata as Record<string, string>)['subscription_id'] && (
                <p className="text-xs text-slate-500">
                  {t('pages:onboarding.credential.subscription', 'Subscription')}: {(selectedCredential.metadata as Record<string, string>)['subscription_id']}
                </p>
              )}
            </div>
          )}

          {/* Cloud credential — for single host */}
          {wizardMode === 'single_host' && selectedPendingHost && singleHostCloudProvider && (
            <div className="space-y-2">
              <Label>{t('pages:onboarding.credential.label', 'Cloud Credential')}</Label>

              {loadingCredentials ? (
                <div className="flex justify-center py-4"><Spinner /></div>
              ) : credentials.length === 0 ? (
                <Alert variant="warning">
                  <AlertDescription>
                    {t('pages:onboarding.credential.noCredentials', 'No active credentials found for this cloud provider.')}
                  </AlertDescription>
                </Alert>
              ) : (
                <Select value={credentialId} onValueChange={setCredentialId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('pages:onboarding.credential.select', 'Select credential...')} />
                  </SelectTrigger>
                  <SelectContent>
                    {credentials.map(cred => (
                      <SelectItem key={cred.id} value={cred.id}>
                        {cred.name} ({cred.credentialType})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {selectedCredential && (selectedCredential.metadata as Record<string, string>)['project_id'] && (
                <p className="text-xs text-slate-500">
                  {t('pages:onboarding.credential.project', 'Project')}: {(selectedCredential.metadata as Record<string, string>)['project_id']}
                </p>
              )}
              {selectedCredential && (selectedCredential.metadata as Record<string, string>)['subscription_id'] && (
                <p className="text-xs text-slate-500">
                  {t('pages:onboarding.credential.subscription', 'Subscription')}: {(selectedCredential.metadata as Record<string, string>)['subscription_id']}
                </p>
              )}
            </div>
          )}

          {/* SSH Key — for single host mode (optional, for platform-managed host access) */}
          {wizardMode === 'single_host' && selectedPendingHost && singleHostCloudProvider && selectedCredential && (
            <div className="space-y-2">
              <Label>{t('pages:onboarding.configure.sshKey', 'SSH Key')}</Label>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {t('pages:onboarding.configure.cloudSshKeyHint', 'Optional. Platform-generated SSH key for host access and debugging.')}
              </p>

              {loadingSshKeys ? (
                <div className="flex justify-center py-4"><Spinner /></div>
              ) : sshKeys.length === 0 ? (
                <p className="text-xs text-slate-400">
                  {t('pages:onboarding.configure.noCloudSshKeys', 'No SSH keys available. Generate one in Security → SSH Keys.')}
                </p>
              ) : (
                <Select value={sshKeyId} onValueChange={setSshKeyId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('pages:onboarding.configure.sshKeySelect', 'Select SSH key...')} />
                  </SelectTrigger>
                  <SelectContent>
                    {sshKeys.map(key => (
                      <SelectItem key={key.id} value={key.id}>
                        {/* eslint-disable-next-line i18next/no-literal-string -- dynamic API data */}
                        {key.name} ({key.keyType}) — {key.fingerprint.substring(0, 20)}...
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* SSH Key — for cloud VMs in host group mode (optional, for platform-managed host access) */}
          {wizardMode === 'host_group' && selectedHostGroup && !needsSSHAccess && selectedCredential && (
            <div className="space-y-2">
              <Label>{t('pages:onboarding.configure.sshKey', 'SSH Key')}</Label>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {t('pages:onboarding.configure.cloudSshKeyHint', 'Optional. Platform-generated SSH key for host access and debugging.')}
              </p>

              {loadingSshKeys ? (
                <div className="flex justify-center py-4"><Spinner /></div>
              ) : sshKeys.length === 0 ? (
                <p className="text-xs text-slate-400">
                  {t('pages:onboarding.configure.noCloudSshKeys', 'No SSH keys available. Generate one in Security → SSH Keys.')}
                </p>
              ) : (
                <Select value={sshKeyId} onValueChange={setSshKeyId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('pages:onboarding.configure.sshKeySelect', 'Select SSH key...')} />
                  </SelectTrigger>
                  <SelectContent>
                    {sshKeys.map(key => (
                      <SelectItem key={key.id} value={key.id}>
                        {/* eslint-disable-next-line i18next/no-literal-string -- dynamic API data */}
                        {key.name} ({key.keyType}) — {key.fingerprint.substring(0, 20)}...
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* SSH Key — for on-prem and on-prem K8s host groups */}
          {selectedHostGroup && needsSSHAccess && (
            <div className="space-y-2">
              <Label>{t('pages:onboarding.configure.sshKey', 'SSH Key')}</Label>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {t('pages:onboarding.configure.sshKeyHint')}
              </p>

              {loadingSshKeys ? (
                <div className="flex justify-center py-4"><Spinner /></div>
              ) : sshKeys.length === 0 ? (
                <Alert variant="warning">
                  <AlertDescription>
                    {t('pages:onboarding.configure.noSshKeys')}
                  </AlertDescription>
                </Alert>
              ) : (
                <Select value={sshKeyId} onValueChange={setSshKeyId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('pages:onboarding.configure.sshKeySelect', 'Select SSH key...')} />
                  </SelectTrigger>
                  <SelectContent>
                    {sshKeys.map(key => (
                      <SelectItem key={key.id} value={key.id}>
                        {/* eslint-disable-next-line i18next/no-literal-string -- dynamic API data */}
                        {key.name} ({key.keyType}) — {key.fingerprint.substring(0, 20)}...
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* Cloud configuration — region, vm size, network (host group mode) */}
          {wizardMode === 'host_group' && selectedCredential && !needsSSHAccess && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('pages:onboarding.configure.region', 'Region')}</Label>
                  <Select value={region} onValueChange={setRegion}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(selectedHostGroup?.cloudProvider === 'upcloud' ? UPCLOUD_ZONES
                        : selectedHostGroup?.cloudProvider === 'azure' ? AZURE_REGIONS
                        : GCP_REGIONS
                      ).map(r => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vm-size-host-group">{t('pages:onboarding.configure.vmSize', 'VM Size')}</Label>
                  {/* Editable: providers ship far more SKUs than we curate
                      (Azure has hundreds), so operators can type any plan
                      string. The list below is the recommended subset; GCP is
                      region-filtered (c2 not in europe-north1 etc.). */}
                  <EditableCombobox
                    id="vm-size-host-group"
                    value={vmSize}
                    onValueChange={chooseVmSize}
                    options={selectedHostGroup?.cloudProvider === 'upcloud' ? UPCLOUD_PLANS
                      : selectedHostGroup?.cloudProvider === 'azure' ? AZURE_VM_SIZES
                      : gcpVmSizesForRegion(region)}
                    placeholder={t('pages:onboarding.configure.vmSizePlaceholder', 'Select or type a custom size')}
                  />
                </div>
              </div>

              {!isKubernetes && (
                <div className="space-y-2">
                  <Label className={`flex items-center gap-2 ${jinaRerankerAvailable ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}>
                    <Checkbox
                      checked={effectiveJinaRerankerEnabled}
                      disabled={!jinaRerankerAvailable}
                      onCheckedChange={(checked) => {
                        if (jinaRerankerAvailable) setJinaRerankerEnabled(checked === true)
                      }}
                    />
                    <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {t('pages:onboarding.configure.jinaReranker', 'Jina reranker')}
                    </span>
                  </Label>
                  <p className="text-xs text-slate-500 dark:text-slate-400 ml-6">
                    {jinaRerankerAvailable
                      ? t('pages:onboarding.configure.jinaRerankerHint', 'Optional cross-encoder reranking for 4+ vCPU intelligence hosts.')
                      : t('pages:onboarding.configure.jinaRerankerSmallHostHint', 'Unavailable on 2 vCPU hosts; choose a 4+ vCPU size to enable it.')}
                  </p>
                </div>
              )}

              {/* Replicas + HA Mode — VM deployments. Azure and UpCloud are
                  forced to 1 because azure-vm and upcloud-vm each build a
                  single VM (Track C lifts Azure).
                  The infra-service enqueue guard enforces the same invariant
                  on the backend; this UI lock is defense-in-depth and, more
                  importantly, stops promising HA in the wizard label. */}
              {!isKubernetes && (() => {
                const isAzure = selectedHostGroup?.cloudProvider === 'azure'
                const isUpcloud = selectedHostGroup?.cloudProvider === 'upcloud'
                const singleVm = isSingletonVmProvider(selectedHostGroup?.cloudProvider)
                const maxReplicas = singleVm ? 1 : 5
                return (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('pages:onboarding.configure.replicas', 'Replicas')}</Label>
                        <Input
                          type="number"
                          min={1}
                          max={maxReplicas}
                          value={replicaCount}
                          disabled={singleVm}
                          onChange={e => {
                            const v = Math.min(maxReplicas, Math.max(1, Number(e.target.value) || 1))
                            setReplicaCount(v)
                          }}
                          className="max-w-[120px]"
                        />
                        <p className="text-xs text-slate-400">
                          {isAzure
                            ? t('pages:onboarding.configure.replicasAzureSingleHint', 'Azure VM deployments are single-VM in this module version.')
                            : isUpcloud
                              ? t('pages:onboarding.configure.replicasUpcloudSingleHint', 'UpCloud VM deployments are single-VM in this module version.')
                              : replicaCount === 1
                                ? t('pages:onboarding.configure.replicasSingleHint', 'Single VM (non-HA)')
                                : t('pages:onboarding.configure.replicasHint', '2–5 instances per host group')}
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label>{t('pages:onboarding.configure.haMode', 'HA Mode')}</Label>
                        <div className="flex items-center h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm text-slate-600 dark:text-slate-300">
                          {singleVm
                            ? t('pages:onboarding.configure.haStandalone', 'Standalone')
                            : selectedHostGroup?.mode === 'active_active'
                              ? t('pages:onboarding.configure.haActiveActive')
                              : t('pages:onboarding.configure.haActivePassive')}
                        </div>
                        <p className="text-xs text-slate-400">
                          {isAzure
                            ? t('pages:onboarding.configure.haModeAzureHint', 'Azure HA arrives in a future module tag.')
                            : isUpcloud
                              ? t('pages:onboarding.configure.haModeUpcloudHint', 'UpCloud HA needs a multi-server module.')
                              : t('pages:onboarding.configure.haModeHint', 'Determined by host group configuration')}
                        </p>
                      </div>
                    </div>

                    {isAzure && (
                      <Alert variant="warning">
                        <AlertDescription>
                          {t('pages:onboarding.configure.azureHaUnavailable', 'The azure-vm Terraform module currently provisions a single VM. Multi-replica HA on Azure is planned for a future module tag. Replica count is locked to 1.')}
                        </AlertDescription>
                      </Alert>
                    )}

                    {isUpcloud && (
                      <Alert variant="warning">
                        <AlertDescription>
                          {t('pages:onboarding.configure.upcloudHaUnavailable', 'The upcloud-vm Terraform module provisions a single server. Replica count is locked to 1; use one environment per server until a multi-server module exists.')}
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                )
              })()}

              {/* Packer image selector — VM deployments only */}
              {!isKubernetes && (
                <div className="space-y-2">
                  <Label>{t('pages:onboarding.configure.packerImage', 'Base Image')}</Label>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {t('pages:onboarding.configure.packerImageHint', 'Select the Packer-built VM image to use as the base.')}
                  </p>
                  {loadingImages ? (
                    <div className="flex justify-center py-4"><Spinner /></div>
                  ) : packerImages.length === 0 ? (
                    <Alert variant="warning">
                      <AlertDescription>
                        {t('pages:onboarding.configure.noPackerImages', 'No available images for this cloud provider. Build one in Infrastructure → Images.')}
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Select value={packerImageId} onValueChange={setPackerImageId}>
                      <SelectTrigger className="max-w-md">
                        <SelectValue placeholder={t('pages:onboarding.configure.packerImagePlaceholder', 'Select image...')} />
                      </SelectTrigger>
                      <SelectContent>
                        {packerImages.map(img => (
                          <SelectItem key={img.id} value={img.id}>
                            {/* eslint-disable-next-line i18next/no-literal-string -- dynamic API data */}
                            {img.name} v{img.version} ({img.cloudProvider})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {/* Managed Database — VM deployments, all cloud providers */}
              {!isKubernetes && selectedHostGroup?.cloudProvider && (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={managedDbEnabled}
                        onCheckedChange={(checked) => {
                          setManagedDbEnabled(checked === true)
                          if (!checked) setSeparateIntelligenceDb(false)
                        }}
                      />
                      <Database size={16} className="text-slate-500" />
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {t('pages:onboarding.configure.managedDb', 'Use managed database')}
                      </span>
                    </Label>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 ml-7">
                    {t('pages:onboarding.configure.managedDbHintGeneric', 'Provisions a managed PostgreSQL instance instead of local PostgreSQL on the VM.')}
                  </p>

                  {managedDbEnabled && (
                    <div className="ml-7 space-y-4 p-3 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                      <div className="space-y-2">
                        <Label>{t('pages:onboarding.configure.dbTier', 'Database Tier')}</Label>
                        <Select value={dbTier} onValueChange={setDbTier}>
                          <SelectTrigger className="max-w-md">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {(selectedHostGroup.cloudProvider === 'upcloud' ? UPCLOUD_DB_PLANS
                              : selectedHostGroup.cloudProvider === 'azure' ? AZURE_DB_SKUS
                              : GCP_DB_TIERS
                            ).map(tier => (
                              <SelectItem key={tier.value} value={tier.value}>{tier.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Separate intelligence database */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={separateIntelligenceDb}
                            onCheckedChange={(checked) => setSeparateIntelligenceDb(checked === true)}
                          />
                          <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            {t('pages:onboarding.configure.separateIntelligenceDb', 'Separate intelligence database')}
                          </span>
                        </Label>
                        <p className="text-xs text-slate-400 ml-6">
                          {t('pages:onboarding.configure.separateIntelligenceDbHint', 'Intelligence services can require significantly more CPU and memory. Use a separate database instance for independent scaling.')}
                        </p>

                        {separateIntelligenceDb && (
                          <div className="ml-6 mt-2 space-y-2">
                            <Label>{t('pages:onboarding.configure.intelligenceDbTier', 'Intelligence Database Tier')}</Label>
                            <Select value={intelligenceDbTier} onValueChange={setIntelligenceDbTier}>
                              <SelectTrigger className="max-w-md">
                                <SelectValue placeholder={t('pages:onboarding.configure.selectDbTier', 'Select tier...')} />
                              </SelectTrigger>
                              <SelectContent>
                                {(selectedHostGroup.cloudProvider === 'upcloud' ? UPCLOUD_DB_PLANS
                                  : selectedHostGroup.cloudProvider === 'azure' ? AZURE_DB_SKUS
                                  : GCP_DB_TIERS
                                ).map(tier => (
                                  <SelectItem key={tier.value} value={tier.value}>{tier.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label>{t('pages:onboarding.configure.networkCidr', 'Network CIDR')}</Label>
                <Input
                  value={networkCidr}
                  onChange={e => setNetworkCidr(e.target.value)}
                  placeholder={t('pages:onboarding.configure.networkCidrPlaceholder')}
                  className="max-w-xs"
                />
              </div>

              {(() => {
                const versions = tfModuleName ? getModuleVersions(terraformModules, tfModuleName) : []
                return versions.length > 0 ? (
                  <div className="space-y-2">
                    <Label>{t('pages:onboarding.configure.tfModuleVersion', 'Terraform Module Version')}</Label>
                    <Select value={tfModuleVersion} onValueChange={setTfModuleVersion}>
                      <SelectTrigger className="max-w-xs">
                        <SelectValue placeholder={t('pages:onboarding.configure.tfModuleVersionPlaceholder', 'Latest')} />
                      </SelectTrigger>
                      <SelectContent>
                        {versions.map(v => (
                          <SelectItem key={v} value={v}>
                            {tfModuleName} {v}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null
              })()}

              {/* Helm Chart — only for Kubernetes deployments */}
              {isKubernetes && chartVersions.length > 0 && (
                <div className="space-y-2">
                  <Label>{t('pages:onboarding.configure.chartVersion', 'Helm Chart Version')}</Label>
                  <Select value={chartVersion} onValueChange={setChartVersion}>
                    <SelectTrigger className="max-w-xs">
                      <SelectValue placeholder={t('pages:onboarding.configure.chartVersionPlaceholder', 'Latest')} />
                    </SelectTrigger>
                    <SelectContent>
                      {chartVersions.map(cv => (
                        <SelectItem key={cv.version} value={cv.version}>
                          {/* eslint-disable-next-line i18next/no-literal-string -- dynamic API data */}
                          {cv.version} (app: {cv.appVersion})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Advanced settings — collapsed by default, sits at the bottom
                  because it contains opt-in overrides that most operators
                  won't touch. Provider-aware: GCP exposes zone/disk/DFS;
                  UpCloud exposes disk/DFS;
                  Azure adds availability zone + DFS replication + diagnostics
                  + log retention + SSH CIDRs. */}
              {(selectedHostGroup?.cloudProvider ?? singleHostCloudProvider) === 'gcp' && (
                <AdvancedSettings
                  provider="gcp"
                  region={region}
                  open={advancedOpen}
                  onOpenChange={setAdvancedOpen}
                  zone={zone}
                  onZoneChange={setZone}
                  diskSizeGb={diskSizeGb}
                  onDiskSizeChange={setDiskSizeGb}
                  dfsEnabled={dfsEnabled}
                  onDfsEnabledChange={setDfsEnabled}
                  t={t}
                />
              )}
              {(selectedHostGroup?.cloudProvider ?? singleHostCloudProvider) === 'azure' && (
                <AdvancedSettings
                  provider="azure"
                  region={region}
                  open={advancedOpen}
                  onOpenChange={setAdvancedOpen}
                  availabilityZones={availabilityZones}
                  onAvailabilityZonesChange={setAvailabilityZones}
                  diskSizeGb={diskSizeGb}
                  onDiskSizeChange={setDiskSizeGb}
                  dfsEnabled={dfsEnabled}
                  onDfsEnabledChange={setDfsEnabled}
                  dfsReplication={dfsReplication}
                  onDfsReplicationChange={setDfsReplication}
                  diagnosticsEnabled={diagnosticsEnabled}
                  onDiagnosticsEnabledChange={setDiagnosticsEnabled}
                  logRetentionDays={logRetentionDays}
                  onLogRetentionDaysChange={setLogRetentionDays}
                  sshAllowedCidrs={sshAllowedCidrs}
                  onSshAllowedCidrsChange={setSshAllowedCidrs}
                  t={t}
                />
              )}
              {(selectedHostGroup?.cloudProvider ?? singleHostCloudProvider) === 'upcloud' && (
                <AdvancedSettings
                  provider="upcloud"
                  region={region}
                  open={advancedOpen}
                  onOpenChange={setAdvancedOpen}
                  includedStorageGb={upcloudPlanStorageGb(vmSize)}
                  diskSizeGb={diskSizeGb}
                  onDiskSizeChange={chooseDiskSize}
                  dfsEnabled={dfsEnabled}
                  onDfsEnabledChange={setDfsEnabled}
                  t={t}
                />
              )}
            </>
          )}

          {/* Cloud configuration — region, vm size, network (single host mode) */}
          {wizardMode === 'single_host' && selectedCredential && singleHostCloudProvider && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('pages:onboarding.configure.region', 'Region')}</Label>
                  <Select value={region} onValueChange={setRegion}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(singleHostCloudProvider === 'upcloud' ? UPCLOUD_ZONES
                        : singleHostCloudProvider === 'azure' ? AZURE_REGIONS
                        : GCP_REGIONS
                      ).map(r => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vm-size-single-host">{t('pages:onboarding.configure.vmSize', 'VM Size')}</Label>
                  {/* See host-group block for rationale; same conditional shape. */}
                  <EditableCombobox
                    id="vm-size-single-host"
                    value={vmSize}
                    onValueChange={chooseVmSize}
                    options={singleHostCloudProvider === 'upcloud' ? UPCLOUD_PLANS
                      : singleHostCloudProvider === 'azure' ? AZURE_VM_SIZES
                      : gcpVmSizesForRegion(region)}
                    placeholder={t('pages:onboarding.configure.vmSizePlaceholder', 'Select or type a custom size')}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className={`flex items-center gap-2 ${jinaRerankerAvailable ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}>
                  <Checkbox
                    checked={effectiveJinaRerankerEnabled}
                    disabled={!jinaRerankerAvailable}
                    onCheckedChange={(checked) => {
                      if (jinaRerankerAvailable) setJinaRerankerEnabled(checked === true)
                    }}
                  />
                  <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {t('pages:onboarding.configure.jinaReranker', 'Jina reranker')}
                  </span>
                </Label>
                <p className="text-xs text-slate-500 dark:text-slate-400 ml-6">
                  {jinaRerankerAvailable
                    ? t('pages:onboarding.configure.jinaRerankerHint', 'Optional cross-encoder reranking for 4+ vCPU intelligence hosts.')
                    : t('pages:onboarding.configure.jinaRerankerSmallHostHint', 'Unavailable on 2 vCPU hosts; choose a 4+ vCPU size to enable it.')}
                </p>
              </div>

              {/* Packer image selector — VM deployments only */}
              <div className="space-y-2">
                <Label>{t('pages:onboarding.configure.packerImage', 'Base Image')}</Label>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t('pages:onboarding.configure.packerImageHint', 'Select the Packer-built VM image to use as the base.')}
                </p>
                {loadingImages ? (
                  <div className="flex justify-center py-4"><Spinner /></div>
                ) : packerImages.length === 0 ? (
                  <Alert variant="warning">
                    <AlertDescription>
                      {t('pages:onboarding.configure.noPackerImages', 'No available images for this cloud provider. Build one in Infrastructure → Images.')}
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Select value={packerImageId} onValueChange={setPackerImageId}>
                    <SelectTrigger className="max-w-md">
                      <SelectValue placeholder={t('pages:onboarding.configure.packerImagePlaceholder', 'Select image...')} />
                    </SelectTrigger>
                    <SelectContent>
                      {packerImages.map(img => (
                        <SelectItem key={img.id} value={img.id}>
                          {/* eslint-disable-next-line i18next/no-literal-string -- dynamic API data */}
                          {img.name} v{img.version} ({img.cloudProvider})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Managed Database */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={managedDbEnabled}
                      onCheckedChange={(checked) => {
                        setManagedDbEnabled(checked === true)
                        if (!checked) setSeparateIntelligenceDb(false)
                      }}
                    />
                    <Database size={16} className="text-slate-500" />
                    <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {t('pages:onboarding.configure.managedDb', 'Use managed database')}
                    </span>
                  </Label>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 ml-7">
                  {t('pages:onboarding.configure.managedDbHintGeneric', 'Provisions a managed PostgreSQL instance instead of local PostgreSQL on the VM.')}
                </p>

                {managedDbEnabled && (
                  <div className="ml-7 space-y-4 p-3 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                    <div className="space-y-2">
                      <Label>{t('pages:onboarding.configure.dbTier', 'Database Tier')}</Label>
                      <Select value={dbTier} onValueChange={setDbTier}>
                        <SelectTrigger className="max-w-md">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(singleHostCloudProvider === 'upcloud' ? UPCLOUD_DB_PLANS
                            : singleHostCloudProvider === 'azure' ? AZURE_DB_SKUS
                            : GCP_DB_TIERS
                          ).map(tier => (
                            <SelectItem key={tier.value} value={tier.value}>{tier.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Separate intelligence database */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={separateIntelligenceDb}
                          onCheckedChange={(checked) => setSeparateIntelligenceDb(checked === true)}
                        />
                        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          {t('pages:onboarding.configure.separateIntelligenceDb', 'Separate intelligence database')}
                        </span>
                      </Label>
                      <p className="text-xs text-slate-400 ml-6">
                        {t('pages:onboarding.configure.separateIntelligenceDbHint', 'Intelligence services can require significantly more CPU and memory. Use a separate database instance for independent scaling.')}
                      </p>

                      {separateIntelligenceDb && (
                        <div className="ml-6 mt-2 space-y-2">
                          <Label>{t('pages:onboarding.configure.intelligenceDbTier', 'Intelligence Database Tier')}</Label>
                          <Select value={intelligenceDbTier} onValueChange={setIntelligenceDbTier}>
                            <SelectTrigger className="max-w-md">
                              <SelectValue placeholder={t('pages:onboarding.configure.selectDbTier', 'Select tier...')} />
                            </SelectTrigger>
                            <SelectContent>
                              {(singleHostCloudProvider === 'upcloud' ? UPCLOUD_DB_PLANS
                                : singleHostCloudProvider === 'azure' ? AZURE_DB_SKUS
                                : GCP_DB_TIERS
                              ).map(tier => (
                                <SelectItem key={tier.value} value={tier.value}>{tier.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>{t('pages:onboarding.configure.networkCidr', 'Network CIDR')}</Label>
                <Input
                  value={networkCidr}
                  onChange={e => setNetworkCidr(e.target.value)}
                  placeholder={t('pages:onboarding.configure.networkCidrPlaceholder')}
                  className="max-w-xs"
                />
              </div>

              {(() => {
                const versions = tfModuleName ? getModuleVersions(terraformModules, tfModuleName) : []
                return versions.length > 0 ? (
                  <div className="space-y-2">
                    <Label>{t('pages:onboarding.configure.tfModuleVersion', 'Terraform Module Version')}</Label>
                    <Select value={tfModuleVersion} onValueChange={setTfModuleVersion}>
                      <SelectTrigger className="max-w-xs">
                        <SelectValue placeholder={t('pages:onboarding.configure.tfModuleVersionPlaceholder', 'Latest')} />
                      </SelectTrigger>
                      <SelectContent>
                        {versions.map(v => (
                          <SelectItem key={v} value={v}>
                            {tfModuleName} {v}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null
              })()}

              {/* Advanced settings — see host_group branch for rationale. */}
              {(selectedHostGroup?.cloudProvider ?? singleHostCloudProvider) === 'gcp' && (
                <AdvancedSettings
                  provider="gcp"
                  region={region}
                  open={advancedOpen}
                  onOpenChange={setAdvancedOpen}
                  zone={zone}
                  onZoneChange={setZone}
                  diskSizeGb={diskSizeGb}
                  onDiskSizeChange={setDiskSizeGb}
                  dfsEnabled={dfsEnabled}
                  onDfsEnabledChange={setDfsEnabled}
                  t={t}
                />
              )}
              {(selectedHostGroup?.cloudProvider ?? singleHostCloudProvider) === 'azure' && (
                <AdvancedSettings
                  provider="azure"
                  region={region}
                  open={advancedOpen}
                  onOpenChange={setAdvancedOpen}
                  availabilityZones={availabilityZones}
                  onAvailabilityZonesChange={setAvailabilityZones}
                  diskSizeGb={diskSizeGb}
                  onDiskSizeChange={setDiskSizeGb}
                  dfsEnabled={dfsEnabled}
                  onDfsEnabledChange={setDfsEnabled}
                  dfsReplication={dfsReplication}
                  onDfsReplicationChange={setDfsReplication}
                  diagnosticsEnabled={diagnosticsEnabled}
                  onDiagnosticsEnabledChange={setDiagnosticsEnabled}
                  logRetentionDays={logRetentionDays}
                  onLogRetentionDaysChange={setLogRetentionDays}
                  sshAllowedCidrs={sshAllowedCidrs}
                  onSshAllowedCidrsChange={setSshAllowedCidrs}
                  t={t}
                />
              )}
              {(selectedHostGroup?.cloudProvider ?? singleHostCloudProvider) === 'upcloud' && (
                <AdvancedSettings
                  provider="upcloud"
                  region={region}
                  open={advancedOpen}
                  onOpenChange={setAdvancedOpen}
                  includedStorageGb={upcloudPlanStorageGb(vmSize)}
                  diskSizeGb={diskSizeGb}
                  onDiskSizeChange={chooseDiskSize}
                  dfsEnabled={dfsEnabled}
                  onDfsEnabledChange={setDfsEnabled}
                  t={t}
                />
              )}
            </>
          )}

          {/* On-prem configuration — bastion, nodes, ssh user */}
          {wizardMode === 'host_group' && selectedHostGroup && needsSSHAccess && sshKeyId && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('pages:onboarding.configure.bastionIp', 'Bastion IP')}</Label>
                  <Input
                    value={bastionIp}
                    onChange={e => setBastionIp(e.target.value)}
                    placeholder={t('pages:onboarding.configure.bastionIpPlaceholder')}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('pages:onboarding.configure.sshUser', 'SSH User')}</Label>
                  <Input
                    value={sshUser}
                    onChange={e => setSshUser(e.target.value)}
                    placeholder={t('pages:onboarding.configure.sshUserPlaceholder')}
                  />
                  <p className="text-xs text-slate-400">{t('pages:onboarding.configure.sshUserDefault', 'Default: agion')}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('pages:onboarding.configure.sudoPassword', 'Sudo Password')}</Label>
                <Input
                  type="password"
                  value={sudoPassword}
                  onChange={e => setSudoPassword(e.target.value)}
                  placeholder={t('pages:onboarding.configure.sudoPasswordPlaceholder', 'Leave empty if SSH user has passwordless sudo')}
                  className="max-w-md"
                />
                <p className="text-xs text-slate-400">
                  {t('pages:onboarding.configure.sudoPasswordHint', 'Required if the SSH user needs a password for sudo. Stored securely in vault.')}
                </p>
              </div>

              <div className="space-y-2">
                <Label>{t('pages:onboarding.configure.nodeIps', 'Node IPs')}</Label>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t('pages:onboarding.configure.nodeIpsHaHint', 'All nodes join as K3s servers with HA embedded etcd (minimum 3 nodes). Mark at least one node with /data mount for database storage.')}
                </p>
                <div className="space-y-2">
                  {nodes.map((node, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <Badge variant="info" className="w-16 justify-center text-xs">
                        {t('pages:onboarding.configure.nodeServerLabel')}
                      </Badge>
                      <Input
                        value={node.ip}
                        onChange={e => updateNodeIp(index, e.target.value)}
                        placeholder={t('pages:onboarding.configure.nodeIpPlaceholder', 'Internal IP address')}
                        className="flex-1"
                      />
                      <Label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 whitespace-nowrap cursor-pointer">
                        <Checkbox
                          checked={node.storage}
                          onCheckedChange={() => toggleStorage(index)}
                        />
                        {t('pages:onboarding.configure.storageNode', '/data')}
                      </Label>
                      {nodes.length > 3 && (
                        <Button variant="ghost" size="sm" onClick={() => removeNode(index)}>
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addNode}>
                    <Plus size={14} className="mr-1" />
                    {t('pages:onboarding.configure.addNode', 'Add Node')}
                  </Button>
                </div>
                {filteredNodes.length >= 3 && (
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="success" className="text-xs">HA</Badge>
                    <span className="text-xs text-slate-500">
                      {t('pages:onboarding.configure.haEtcdInfo', '{{count}} server nodes with embedded etcd', { count: filteredNodes.length })}
                    </span>
                  </div>
                )}
                {filteredNodes.length > 0 && filteredNodes.length < 3 && (
                  <Alert variant="warning">
                    <AlertDescription>
                      {t('pages:onboarding.configure.haMinNodes', 'HA etcd requires minimum 3 nodes. Add more nodes for high availability.')}
                    </AlertDescription>
                  </Alert>
                )}
                {filteredNodes.length >= 3 && !hasStorageNode && (
                  <Alert variant="warning">
                    <AlertDescription>
                      {t('pages:onboarding.configure.noStorageNode', 'At least one node must be marked with /data mount for database storage.')}
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              {(() => {
                const k3sVersions = getModuleVersions(terraformModules, 'k3s-cluster')
                return k3sVersions.length > 0 ? (
                  <div className="space-y-2">
                    <Label>{t('pages:onboarding.configure.tfModuleVersion', 'Terraform Module Version')}</Label>
                    <Select value={tfModuleVersion} onValueChange={setTfModuleVersion}>
                      <SelectTrigger className="max-w-xs">
                        <SelectValue placeholder={t('pages:onboarding.configure.tfModuleVersionPlaceholder', 'Latest')} />
                      </SelectTrigger>
                      <SelectContent>
                        {k3sVersions.map(v => (
                          // eslint-disable-next-line i18next/no-literal-string -- dynamic API data
                          <SelectItem key={v} value={v}>
                            k3s-cluster {v}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null
              })()}

              {chartVersions.length > 0 && (
                <div className="space-y-2">
                  <Label>{t('pages:onboarding.configure.chartVersion', 'Helm Chart Version')}</Label>
                  <Select value={chartVersion} onValueChange={setChartVersion}>
                    <SelectTrigger className="max-w-xs">
                      <SelectValue placeholder={t('pages:onboarding.configure.chartVersionPlaceholder', 'Latest')} />
                    </SelectTrigger>
                    <SelectContent>
                      {chartVersions.map(cv => (
                        <SelectItem key={cv.version} value={cv.version}>
                          {/* eslint-disable-next-line i18next/no-literal-string -- dynamic API data */}
                          {cv.version} (app: {cv.appVersion})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </>
          )}
        </div>

        {/* Deploy button */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <Button onClick={() => void handleDeploy()} disabled={!canDeploy || isDeploying}>
            {isDeploying ? (
              <Spinner className="h-4 w-4 mr-2" />
            ) : (
              <Rocket className="h-4 w-4 mr-2" />
            )}
            {isDeploying
              ? t('pages:onboarding.deploying', 'Deploying...')
              : t('pages:onboarding.deploy', 'Deploy Environment')}
          </Button>
        </div>
      </Card>
    </div>
  )
}
